<template>
  <AutoFitContainer class="w-full h-full flex flex-col p-4">
    <!-- Header -->
    <div class="flex justify-between items-center p-4">
      <div class="text-2xl font-bold">AI Agents</div>
      <el-button type="primary" icon="el-icon-plus" @click="handleEdit()">
        Create Agent
      </el-button>
    </div>
    <el-divider />
    <!-- Filter Bar -->
    <div
      class="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4"
    >
      <!-- 搜索框 -->
      <div class="flex flex-row max-w-sm gap-2 w-full md:w-auto">
        <el-input
          v-model="filters.name"
          clearable
          placeholder="Search agents..."
          class="flex-1"
        />
        <el-button @click="() => tableCtrl.refresh()" icon="el-icon-search" />
      </div>
      <!-- 筛选器 -->
      <div
        class="flex flex-row flex-wrap gap-2 w-full md:w-auto justify-between md:justify-end"
      >
        <div class="w-48 min-w-[120px]">
          <SmartSelect
            v-model="filters.status"
            :options="enumToOptions(AIAgentStatus)"
            :add-all-option="{ value: 'All Statuses', key: undefined }"
            clearable
            placeholder="Status"
          />
        </div>
        <div class="w-48 min-w-[120px]">
          <SmartSelect
            v-model="filters.type"
            :options="enumToOptions(AIAgentType)"
            :add-all-option="{ value: 'All Types', key: undefined }"
            clearable
            placeholder="Type"
          />
        </div>
        <div class="w-48 min-w-[120px]">
          <SmartSelect
            v-model="filters.model"
            :options="enumToOptions(AIAgentModel)"
            :add-all-option="{ value: 'All Models', key: undefined }"
            clearable
            placeholder="Model"
          />
        </div>
        <div>
          <el-button @click="() => tableCtrl.refresh()" icon="el-icon-refresh">
            Refresh
          </el-button>
        </div>
      </div>
    </div>

    <!-- Tabs: Grid/List -->
    <!-- <div class="mb-4">
      <el-radio-group v-model="tabIndex" size="small">
        <el-radio-button :label="0">Grid View</el-radio-button>
        <el-radio-button :label="1">List View</el-radio-button>
      </el-radio-group>
    </div> -->
    <div
      v-loading="tableCtrl.state.loading"
      element-loading-background="rgba(0, 0, 0, 0.8)"
      class="flex-1 relative"
    >
      <!--卡片视图-->
      <el-scrollbar v-if="tabIndex == 0" class="absolute inset-0 scrollbar">
        <template v-if="tableCtrl.state.tableData.length > 0">
          <div class="grid grid-cols-3 gap-4">
            <div
              v-for="agent of tableCtrl.state.tableData"
              :key="agent.agent_id"
            >
              <AiAgentGridItem :item="agent" @command="handleMenuCommand" />
            </div>
          </div>
        </template>
        <template v-else>
          <Empty class="absolute w-full h-full" />
        </template>
      </el-scrollbar>
      <!--列表-->
      <el-table
        v-else
        :data="tableCtrl.state.tableData"
        class="absolute inset-0"
        height="auto"
      >
        <el-table-column prop="name" label="Agent">
          <template slot-scope="scope">
            <div class="flex flex-row items-center">
              <el-avatar
                :src="scope.row.avatar_url"
                size="small"
                class="mr-2"
              />
              <el-tooltip :content="scope.row.name" placement="top">
                <span
                  class="flex-1 overflow-hidden text-ellipsis whitespace-nowrap"
                >
                  {{ scope.row.name }}
                </span>
              </el-tooltip>
            </div>
          </template>
        </el-table-column>
        <el-table-column prop="base_model" label="Model" />
        <el-table-column prop="status" label="Status">
          <template slot-scope>
            <span>active</span>
          </template>
        </el-table-column>
        <el-table-column prop="knowledgeBase" label="Knowledge">
          <template slot-scope="scope">
            <span>{{ getDocs(scope.row).length }} files</span>
          </template>
        </el-table-column>
        <el-table-column label="Actions">
          <template slot-scope="scope">
            <div class="flex flex-row gap-2">
              <div>
                <el-button
                  size="mini"
                  icon="el-icon-chat-line-square"
                  @click="openChatDialog(scope.row)"
                />
              </div>
              <div>
                <el-button
                  size="mini"
                  icon="el-icon-edit"
                  @click="handleEdit(scope.row)"
                />
              </div>
              <AiAgentMoreDropdown
                class="justify-end"
                :item="scope.row"
                @command="handleMenuCommand"
              />
            </div>
          </template>
        </el-table-column>
      </el-table>
    </div>
    <div class="flex mt-4 justify-end">
      <el-pagination
        @size-change="(val) => tableCtrl.setPageSize(val)"
        @current-change="(val) => tableCtrl.setCurrentPage(val)"
        :current-page="tableCtrl.state.pagination.page"
        :page-sizes="[9, 10, 20, 50, 100]"
        :page-size="tableCtrl.state.pagination.pageSize"
        layout="total, sizes, prev, pager, next, jumper"
        :total="tableCtrl.state.pagination.total"
      >
      </el-pagination>
    </div>
  </AutoFitContainer>
</template>

<script>
import { TableController } from "@/controllers/TableController";
import AutoFitContainer from "@/components/AutoFitContainer.vue";
import SmartSelect from "@/components/SmartControls/SmartSelect.vue";
import {
  AIAgentStatus,
  AIAgentType,
  AIAgentModel,
  enumToOptions,
} from "@/enums";
import AiAgentGridItem from "./components/AiAgentGridItem.vue";
import { deleteWebsocketShadowInstance, deleteAgent } from "@/api";
import AiAgentMoreDropdown from "./components/AiAgentMoreDropdown.vue";
import { delegateLoadTable } from "@/controllers/TableControllerUtils";
export default {
  components: {
    AutoFitContainer,
    SmartSelect,
    AiAgentGridItem,
    AiAgentMoreDropdown,
  },
  name: "AiAgentListPage",
  data() {
    const me = this;
    return {
      AIAgentStatus,
      AIAgentType,
      AIAgentModel,
      tableCtrl: new TableController({
        loadMethod: (opt) =>
          delegateLoadTable(
            me.$store,
            "agentStore/fetchAgents",
            "agentStore/agents",
            opt
          ),
        autoLoad: true,
        isClientPaged: true,
        pageSize: 9,
        keyField: "agent_id",
      }),
      filters: {
        name: "",
        status: "",
        type: "",
        model: "",
      },
      tabIndex: 0,
      createTab: "basic",
      newAgent: {
        name: "",
        type: "",
        description: "",
        // 其它字段
      },
      // 其它弹窗控制变量...
    };
  },
  computed: {},
  methods: {
    enumToOptions,
    openPromptDialog() {
      /* ... */
    },
    openCollaborationDialog() {
      /* ... */
    },
    openAccessDialog() {
      /* ... */
    },
    createAgent() {
      /* ... */
    },
    kbCount(row) {
      return row.knowledgeBase.length;
    },
    collabCount(row) {
      return row.collaborations.length;
    },
    getDocs(row) {
      // 找到 get_agent_knowledge function 并返回 rag.docs
      const fn = row?.functions?.find(
        (f) => f.function_id === "get_agent_knowledge"
      );
      return fn && fn.rag ? fn.rag.docs : [];
    },
    async handleMenuCommand({ command, item }) {
      const me = this;
      switch (command) {
        case "chat":
          me.openChatDialog(item);
          break;
        case "configure":
          me.handleEdit(item);
          break;
        case "knowledge":
          me.openKnowledgeDialog(item);
          break;
        case "prompt":
          break;
        case "collab":
          break;
        case "access":
          break;
        case "share":
          me.handleShare(item);
          break;
        case "delete":
          me.handleDelete(item);
          break;
      }
    },
    async handleShare(item) {
      const me = this;
      await me.$loaderWindow({
        title: `Share Settings for ${item.name}`,
        component: () => import("./components/AiAgentShareSettings.vue"),
        closeOnClickModal: false,
        closeOnPressEscape: false,
        width: "500px",
        props: {
          value: item,
        },
      });
      await me.handleRefresh();
    },
    async openKnowledgeDialog(item) {
      const me = this;
      await me.$loaderWindow({
        title: `Knowledge Base for ${item.name}`,
        component: () => import("./components/EditKnowledgeBaseDialog.vue"),
        closeOnClickModal: false,
        closeOnPressEscape: false,
        props: {
          agent: item,
        },
      });
      await me.$message({
        type: "success",
        message: `Knowledge base updated successfully!`,
      });
      await me.handleRefresh();
    },
    async handleRefresh() {
      await this.tableCtrl.refresh();
    },
    /**
     * 编辑agent
     */
    async handleEdit(item) {
      const me = this;
      await me.$loaderWindow({
        title: `${item ? "Edit" : "Create"} AI Agent`,
        component: () => import("./components/EditAiAgentDialog.vue"),
        closeOnClickModal: false,
        closeOnPressEscape: false,
        props: {
          value: item,
        },
      });

      await me.$message({
        type: "success",
        message: `${item ? "Edited" : "Created"} AI Agent successfully!`,
      });
      // 项id是服务器生成的，重新加载数据
      await me.handleRefresh();
    },
    /**
     * 删除agent
     */
    async handleDelete(item) {
      if (item?.agent_id == undefined)
        throw new Error("Agent ID is required for deletion");
      await this.$confirm(
        `Are you sure you want to delete the agent "${item.name}"?`,
        "Warning",
        {
          confirmButtonText: "Delete",
          cancelButtonText: "Cancel",
          type: "warning",
        }
      );
      if (item.websocket) {
        await deleteWebsocketShadowInstance(item.websocket);
      }
      await deleteAgent(item.agent_id);
      this.$message({
        type: "success",
        message: `Deleted agent "${item.name}" successfully!`,
      });
      await this.handleRefresh();
    },
    async openChatDialog(item) {
      await this.$loaderWindow({
        title: `Chat with ${item.name}`,
        component: () => import("./components/ChatDialog.vue"),
        height: "80%",
        width: "70%",
        showHeader: false,
        padding: false,
        closeOnClickModal: false,
        closeOnPressEscape: false,
        props: {
          agent: item,
        },
      });
    },
  },
  watch: {
    "filters.name"(newVal) {
      this.tableCtrl.setQueryParameters({ name: `%${newVal}%` });
    },
    "filters.status"(newVal) {
      this.tableCtrl.setQueryParameters({ status: newVal });
    },
    "filters.type"(newVal) {
      this.tableCtrl.setQueryParameters({ "config.type": newVal });
    },
    "filters.model"(newVal) {
      this.tableCtrl.setQueryParameters({ base_model: newVal });
    },
  },
};
</script>
